import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  getApartment,
  getPriceHistory
} from '@services/livability/apartments'
import {
  apartmentAnalyticsProps,
  trackEvent
} from '@services/livability/analytics'
import { getApartmentGalleryUrls } from '@services/livability/images'
import { getNearbyPlaces } from '@services/livability/places'
import type {
  Apartment,
  IndexCategory,
  NearbyPlacesResponse,
  PriceHistory
} from '@services/livability/types'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import {
  ArrowLeft,
  Baby,
  Building2,
  Calendar,
  ChevronRight,
  Coffee,
  Compass,
  Cross,
  DoorOpen,
  Dumbbell,
  GraduationCap,
  ListChecks,
  MapPin,
  Maximize2,
  Music,
  ParkingCircle,
  PawPrint,
  Sofa,
  Sparkles,
  Star,
  Store,
  Train,
  Trees,
  Truck,
  Users,
  UtensilsCrossed,
  Wallet,
  Wrench,
  Zap
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn, formatPrice } from '@/lib/utils'
import { AnimatedPage } from '@components/animated'

import { ApartmentGallery } from '../components/ApartmentGallery'
import { ApartmentIndexDisplay } from '../components/ApartmentIndexDisplay'
import { ApartmentPriceChart } from '../components/ApartmentPriceChart'
import { useLivabilityPaths } from '../utils/paths'
import { useLikeApartment } from '../utils/useLikeApartment'

const RATING_CATEGORIES: {
  value: IndexCategory
  labelKey: string
  icon: LucideIcon
}[] = [
  { value: 'general', labelKey: 'detail.categories.general', icon: Sparkles },
  { value: 'budget', labelKey: 'detail.categories.budget', icon: Wallet },
  { value: 'family', labelKey: 'detail.categories.family', icon: Users },
  { value: 'student', labelKey: 'detail.categories.student', icon: GraduationCap }
]

function DetailBreadcrumb({ current }: { current?: string }) {
  const navigate = useNavigate()
  const { t } = useTranslation('livability')
  const paths = useLivabilityPaths()

  return (
    <div className="flex min-w-0 items-center gap-3">
      <button
        type="button"
        onClick={() => navigate(-1)}
        aria-label={t('detail.back')}
        className="flex h-9 w-9 shrink-0 appearance-none items-center justify-center rounded-full border border-ink/15 bg-white p-0 text-ink transition-colors hover:border-ink/30 hover:bg-ink/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
      >
        <ArrowLeft className="h-4 w-4" />
      </button>
      <nav className="flex min-w-0 items-center gap-1.5 text-sm">
        <Link
          to={paths.list}
          className="shrink-0 text-muted-foreground transition-colors hover:text-primary"
        >
          {t('detail.breadcrumbList')}
        </Link>
        {current && (
          <>
            <ChevronRight className="h-4 w-4 shrink-0 text-ink/30" />
            <span className="truncate font-semibold text-ink">{current}</span>
          </>
        )}
      </nav>
    </div>
  )
}

export function ApartmentDetail() {
  const { id } = useParams()
  const { t } = useTranslation('livability')
  const paths = useLivabilityPaths()
  const [apartment, setApartment] = useState<Apartment | null>(null)
  const [nearbyPlaces, setNearbyPlaces] = useState<NearbyPlacesResponse | null>(
    null
  )
  const [priceHistory, setPriceHistory] = useState<PriceHistory[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [placesLoading, setPlacesLoading] = useState(false)
  const [priceHistoryLoading, setPriceHistoryLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [placesError, setPlacesError] = useState<string | null>(null)
  const [indexCategory, setIndexCategory] = useState<IndexCategory>('general')
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  )

  const apartmentId = id ? parseInt(id, 10) : null
  const handleLikeApartment = useLikeApartment(apartment, indexCategory, 'detail')

  useEffect(() => {
    if (!apartmentId) return

    const fetchApartment = async () => {
      try {
        const data = await getApartment(apartmentId, true, indexCategory)
        setApartment(data)
        trackEvent(
          'apartment_viewed',
          apartmentAnalyticsProps(data, 'detail', indexCategory)
        )
        setError(null)
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load apartment'
        )
      } finally {
        setIsLoading(false)
      }
    }

    fetchApartment()
  }, [apartmentId, indexCategory])

  useEffect(() => {
    if (!apartment) return

    const fetchNearbyPlaces = async () => {
      setPlacesLoading(true)
      try {
        const data = await getNearbyPlaces(apartment.id, 500)
        setNearbyPlaces(data)
        setPlacesError(null)
      } catch (err) {
        setPlacesError(
          err instanceof Error ? err.message : 'Failed to load nearby places'
        )
      } finally {
        setPlacesLoading(false)
      }
    }

    fetchNearbyPlaces()
  }, [apartment])

  useEffect(() => {
    if (!apartment) return

    const fetchPriceHistory = async () => {
      setPriceHistoryLoading(true)
      try {
        const data = await getPriceHistory(apartment.id)
        setPriceHistory(data)
      } catch {
      } finally {
        setPriceHistoryLoading(false)
      }
    }

    fetchPriceHistory()
  }, [apartment])

  if (isLoading) {
    return (
      <div className="app-chrome space-y-6">
        <DetailBreadcrumb />
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
            <p className="text-muted-foreground">{t('detail.loading')}</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !apartment) {
    return (
      <div className="app-chrome space-y-6">
        <DetailBreadcrumb />
        <Card className="mx-auto w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-destructive">
              {t('detail.error')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p>{error || t('detail.notFound')}</p>
            <Button asChild className="mt-4">
              <Link to={paths.list}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('detail.backToList')}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const galleryImages = getApartmentGalleryUrls(apartment)

  const detailItems = [
    {
      icon: Wrench,
      label: t('detail.details.condition'),
      value: apartment.condition
    },
    {
      icon: Sofa,
      label: t('detail.details.furnishing'),
      value: apartment.furnishing
    },
    {
      icon: Building2,
      label: t('detail.details.buildingType'),
      value: apartment.building_type
    },
    {
      icon: Compass,
      label: t('detail.details.orientation'),
      value: apartment.orientation
    },
    {
      icon: ParkingCircle,
      label: t('detail.details.parking'),
      value: apartment.parking_type
    },
    {
      icon: PawPrint,
      label: t('detail.details.pets'),
      value: apartment.pets_allowed
    },
    {
      icon: Zap,
      label: t('detail.details.energyClass'),
      value: apartment.energy_class
    },
    {
      icon: Calendar,
      label: t('detail.details.availableFrom'),
      value: apartment.available_from
    }
  ].filter((item) => Boolean(item.value))

  const placeCategories = [
    { key: 'schools' as const, icon: GraduationCap },
    { key: 'grocery_stores' as const, icon: Store },
    { key: 'parks' as const, icon: Trees },
    { key: 'public_transport' as const, icon: Train },
    { key: 'couriers' as const, icon: Truck },
    { key: 'kindergartens' as const, icon: Baby },
    { key: 'universities' as const, icon: Building2 },
    { key: 'gyms' as const, icon: Dumbbell },
    { key: 'restaurants' as const, icon: UtensilsCrossed },
    { key: 'cafes' as const, icon: Coffee },
    { key: 'pharmacies' as const, icon: Cross },
    { key: 'night_clubs' as const, icon: Music }
  ]

  return (
    <AnimatedPage className="app-chrome space-y-6">
      <DetailBreadcrumb current={apartment.title} />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <span
            aria-hidden
            className="mt-1 h-7 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-primary to-peach sm:h-9"
          />
          <div className="min-w-0">
            <h1 className="m-0 font-display text-[1.75rem] font-bold leading-tight tracking-tight text-ink sm:text-[2.25rem]">
              {apartment.title}
            </h1>
            <p className="mt-2 flex items-start gap-2 text-[15px] leading-relaxed text-muted-foreground">
              <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>
                {apartment.street_address && (
                  <span className="font-medium text-foreground">
                    {apartment.street_address},{' '}
                  </span>
                )}
                {apartment.location}
                {apartment.geocode_precision === 'APPROXIMATE' && (
                  <span className="ml-1 text-xs italic">
                    ({t('detail.approximateLocation')})
                  </span>
                )}
              </span>
            </p>
          </div>
        </div>
        <Button onClick={handleLikeApartment} className="shrink-0">
          {t('detail.like')}
        </Button>
      </div>

      <ApartmentGallery images={galleryImages} title={apartment.title} />

      <Card>
        <CardHeader>
          <CardTitle>{t('detail.category.title')}</CardTitle>
          <CardDescription>{t('detail.category.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div
            role="group"
            aria-label={t('detail.category.title')}
            className="grid grid-cols-2 gap-1 rounded-2xl bg-ink/[0.04] p-1 sm:inline-flex sm:flex-wrap"
          >
            {RATING_CATEGORIES.map(({ value, labelKey, icon: Icon }) => {
              const active = indexCategory === value
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setIndexCategory(value)}
                  className={cn(
                    'inline-flex cursor-pointer appearance-none items-center justify-center gap-1.5 rounded-xl border-0 px-4 py-2 text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
                    active
                      ? 'bg-primary text-primary-foreground shadow-[0_4px_12px_rgba(17,173,187,0.35)]'
                      : 'bg-transparent text-ink/60 hover:text-ink'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {t(labelKey)}
                </button>
              )
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {apartment.index && (
            <ApartmentIndexDisplay index={apartment.index} compact={false} />
          )}

          <Card>
            <CardHeader>
              <CardTitle>{t('detail.details.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
                {detailItems.map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <span className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {label}
                      </span>
                      <p className="mt-0.5 text-base font-medium text-ink">
                        {value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {apartment.equipment && (
                <div className="mt-6 flex items-start gap-3 border-t border-ink/[0.06] pt-5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <ListChecks className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <span className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {t('detail.details.equipment')}
                    </span>
                    <p className="mt-0.5 text-base font-medium text-ink">
                      {apartment.equipment}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {priceHistoryLoading ? (
            <Card>
              <CardContent className="py-8 text-center">
                <div
                  className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-primary"
                />
                <p className="mt-4 text-muted-foreground">
                  {t('detail.loadingPriceHistory')}
                </p>
              </CardContent>
            </Card>
          ) : priceHistory.length > 0 ? (
            <ApartmentPriceChart priceHistory={priceHistory} />
          ) : null}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('detail.costs.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between border-b border-ink/[0.06] pb-3">
                <span className="text-muted-foreground">
                  {t('detail.costs.monthlyRent')}
                </span>
                <span className="text-lg font-semibold">
                  {formatPrice(apartment.price_month)}
                </span>
              </div>

              {!!apartment.deposit_amount && (
                <div className="flex items-center justify-between border-b border-ink/[0.06] pb-3">
                  <span className="text-muted-foreground">
                    {t('detail.costs.deposit')}
                  </span>
                  <span className="text-lg font-semibold">
                    €{apartment.deposit_amount}
                  </span>
                </div>
              )}

              {!!apartment.energy_costs && (
                <div className="flex items-center justify-between border-b border-ink/[0.06] pb-3">
                  <span className="text-muted-foreground">
                    {t('detail.costs.energy')}
                  </span>
                  <span className="text-lg font-semibold">
                    €{apartment.energy_costs}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between pt-2">
                <span className="font-semibold">{t('detail.costs.total')}</span>
                <span className="font-display text-xl font-bold text-primary">
                  {apartment.price_month > 0
                    ? formatPrice(
                        apartment.price_month + (apartment.energy_costs || 0)
                      )
                    : formatPrice(null)}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('detail.specsTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col items-center justify-center rounded-2xl bg-ink/[0.03] p-4 text-center">
                  <Maximize2 className="mb-2 h-6 w-6 text-primary" />
                  <span className="font-display text-2xl font-bold">
                    {apartment.area_m2}
                  </span>
                  <span className="text-sm text-muted-foreground">m²</span>
                </div>
                <div className="flex flex-col items-center justify-center rounded-2xl bg-ink/[0.03] p-4 text-center">
                  <DoorOpen className="mb-2 h-6 w-6 text-primary" />
                  <span className="font-display text-2xl font-bold">
                    {apartment.room_count}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {t('detail.units.rooms')}
                  </span>
                </div>
                {apartment.floor && (
                  <div className="flex flex-col items-center justify-center rounded-2xl bg-ink/[0.03] p-4 text-center">
                    <Building2 className="mb-2 h-6 w-6 text-primary" />
                    <span className="font-display text-base font-bold leading-snug">
                      {apartment.floor}
                    </span>
                  </div>
                )}
                {!!apartment.year_built && (
                  <div className="flex flex-col items-center justify-center rounded-2xl bg-ink/[0.03] p-4 text-center">
                    <Calendar className="mb-2 h-6 w-6 text-primary" />
                    <span className="font-display text-2xl font-bold">
                      {apartment.year_built}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {t('detail.units.year')}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

        </div>
      </div>

      {placesLoading && (
        <Card>
          <CardContent className="py-8 text-center">
            <div
              className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-primary"
            />
            <p className="mt-4 text-muted-foreground">
              {t('detail.nearby.loading')}
            </p>
          </CardContent>
        </Card>
      )}

      {placesError && (
        <Card>
          <CardContent className="py-8 text-center text-red-500">
            {placesError}
          </CardContent>
        </Card>
      )}

      {nearbyPlaces && !placesLoading && (
        <div className="pt-4">
          <h2 className="mb-8 font-display text-2xl font-bold tracking-tight text-ink">
            {t('detail.nearby.title')}
          </h2>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {placeCategories.map((category) => {
              const places = nearbyPlaces[category.key]
              const Icon = category.icon

              if (!places || places.length === 0) return null

              const isExpanded = expandedCategories.has(category.key)
              const displayedPlaces = isExpanded ? places : places.slice(0, 3)

              return (
                <Card key={category.key} className="flex flex-col">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="font-display">
                        {t(`detail.places.${category.key}`)}
                      </span>
                      <span className="ml-auto rounded-full bg-ink/[0.05] px-2 py-0.5 text-xs font-medium text-ink/60">
                        {places.length}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col pt-0">
                    <div className="space-y-2">
                      {displayedPlaces.map((place) => (
                        <div
                          key={place.id}
                          className="flex items-center justify-between gap-3 rounded-xl bg-ink/[0.03] px-3 py-2"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-ink">
                              {place.name || 'N/A'}
                            </p>
                            {place.address && (
                              <p className="truncate text-xs text-muted-foreground">
                                {place.address}
                              </p>
                            )}
                            {place.rating != null && (
                              <span className="mt-0.5 flex items-center gap-1 text-xs font-medium text-ink/70">
                                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                                {place.rating.toFixed(1)}
                                {place.user_ratings_total
                                  ? ` (${place.user_ratings_total})`
                                  : ''}
                              </span>
                            )}
                          </div>
                          {place.distance_meters != null && (
                            <span className="shrink-0 text-sm font-semibold text-ink">
                              {place.distance_meters} m
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    {places.length > 3 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-3 w-full"
                        onClick={() =>
                          setExpandedCategories((prev) => {
                            const next = new Set(prev)
                            if (next.has(category.key)) {
                              next.delete(category.key)
                            } else {
                              next.add(category.key)
                            }
                            return next
                          })
                        }
                      >
                        {isExpanded
                          ? t('detail.nearby.showLess')
                          : t('detail.nearby.showAll', { count: places.length })}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      )}
    </AnimatedPage>
  )
}
